/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import {
  MindMapObjectives,
  NextSlidePreview,
  PresentationControls,
  PresentationFooter,
  PresentationHeader,
  PresentationMindMapSection,
  PresentationProgress,
  PresentationTimeline,
  SlideNavigation,
  SlideViewer,
} from '@/components'
import {
  getLessonQueryOptions,
  getUserLessonQueryOptions,
} from '@/requestObjects'
import { useBuildColourMap, useRTL } from '@/hooks'
import { useCallback, useMemo } from 'react'

import type { Language } from '@/types'
import { createFileRoute } from '@tanstack/react-router'
import { useAuthHeaders } from '@/providers'
import { usePresentationKeyboard } from '@/hooks/usePresentationKeyboard'
import { usePresentationState } from '@/hooks/usePresentationState'
import { usePresentationSync } from '@/hooks/usePresentationSync'
import { useQueries } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import z from 'zod'

// Define the search params schema with Zod
const presentationSearchSchema = z.object({
  slide: z.number().default(1),
})

type PresentationSearch = z.infer<typeof presentationSearchSchema>

export const Route = createFileRoute(
  '/_authRoutes/lesson/$lessonId/presentation',
)({
  component: Presentation,
  validateSearch: (search: Record<string, unknown>): PresentationSearch => {
    return presentationSearchSchema.parse(search)
  },
})

function Presentation() {
  const { lessonId } = Route.useParams()
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const { isRTL } = useRTL()
  const headers = useAuthHeaders()

  const [
    {
      data: userLessonPlan,
      isLoading: userLessonPlanLoading,
      error: userLessonPlanError,
    },
    { data: lessonMetadata, isLoading: lessonLoading, error: lessonError },
  ] = useQueries({
    queries: [
      {
        ...getUserLessonQueryOptions({
          lessonId,
          headers,
          language: language as Language,
        }),
      },
      {
        ...getLessonQueryOptions({ lessonId, headers }),
      },
    ],
  })

  const slideImages = lessonMetadata?.s3_uris || []
  const totalSlides = slideImages.length

  const {
    currentSlide,
    setCurrentSlide,
    isPresentationActive,
    syncStatus,
    setSyncStatus,
    audienceConnected,
    setAudienceConnected,
    nextSlide: nextSlideState,
    prevSlide: prevSlideState,
    goToSlide,
    startPresentation,
    stopPresentation,
  } = usePresentationState({ totalSlides, lessonId })

  const { updateAudienceView } = usePresentationSync({
    currentSlide,
    setCurrentSlide,
    setSyncStatus,
    setAudienceConnected,
    audienceConnected,
    totalSlides,
  })

  // Wrap slide navigation functions to also update audience
  const nextSlide = useCallback(() => {
    const newSlide = nextSlideState()
    updateAudienceView(newSlide)
    return newSlide
  }, [nextSlideState, updateAudienceView])

  const prevSlide = useCallback(() => {
    const newSlide = prevSlideState()
    updateAudienceView(newSlide)
    return newSlide
  }, [prevSlideState, updateAudienceView])

  const goToSlideWithSync = useCallback(
    (slideNumber: number) => {
      const newSlide = goToSlide(slideNumber)
      updateAudienceView(newSlide)
    },
    [goToSlide, updateAudienceView],
  )

  // Set up keyboard shortcuts
  usePresentationKeyboard({
    nextSlide,
    prevSlide,
  })

  const totalDuration = useMemo(() => {
    return (
      lessonMetadata?.lesson_plans?.[0]?.lesson_plan_sections?.reduce(
        (acc, { duration = 0 }) => acc + (duration ? +duration : 0),
        0,
      ) || 0
    )
  }, [lessonMetadata])

  const slideColorMap = useBuildColourMap({ userLessonPlan, slideImages })
  const loading = userLessonPlanLoading || lessonLoading
  const error = userLessonPlanError || lessonError

  if (loading) {
    return <div>{t('common.loading')}</div>
  }
  if (error) {
    return <div>{t('common.errorWithMessage', { message: error.message })}</div>
  }

  return (
    <div className="@container h-[100vh] w-full grid grid-rows-[auto_1fr_auto] grid-cols-[1fr_auto] bg-gray-900 text-white dark">
      <div className="col-span-2">
        <PresentationHeader
          isPresentationActive={isPresentationActive}
          audienceConnected={audienceConnected}
          syncStatus={syncStatus}
        />
        <PresentationProgress
          currentSlide={currentSlide}
          totalSlides={totalSlides}
        />
      </div>

      {/* Main Content */}

      <div className="max-h-full flex flex-col lg:grid lg:grid-cols-[minmax(auto,40%)_1fr] xl:grid-rows-[auto_1fr] gap-4 overflow-y-scroll p-4">
        {/* Current Slide */}
        <div className="col-span-1 lg:order-2">
          <div className="w-full font-semibold">
            {t('presentation.currentSlide')}
          </div>
          <div className=" bg-white rounded-lg max-h-[calc(100vh-10rem)] aspect-[4/3]">
            <SlideViewer
              currentSlide={currentSlide}
              totalSlides={totalSlides}
              slideImages={slideImages}
              imgClassName="rounded-lg object-contain max-h-[calc(100vh-10rem)]"
            />
          </div>
        </div>
        <div className="col-span-1">
          <PresentationTimeline
            user_lesson_plan_sections={
              userLessonPlan?.user_lesson_plan_sections || []
            }
            totalDuration={totalDuration}
            goToSlide={goToSlideWithSync}
            index={slideColorMap[currentSlide]?.index}
          />
        </div>

        {slideColorMap[currentSlide]?.index >= -1 && (
          <div
            className="col-span-2 order-3  prose-xl prose-headings:mt-0 pl-2 rtl:pr-2 rtl:pl-0"
            style={
              {
                '--lesson-color': slideColorMap[currentSlide]?.color,
              } as React.CSSProperties
            }
          >
            {slideColorMap[currentSlide]?.index === -1 ? (
              <MindMapObjectives
                objectives={userLessonPlan?.objectives}
                className="border-b-0 border-[var(--lesson-color)] border-l-5 pl-3 -ml-3 rtl:pl-0 rtl:-mr-3 rtl:pr-3 rtl:border-r-5 rtl:border-l-0"
              />
            ) : (
              <PresentationMindMapSection
                {...userLessonPlan?.user_lesson_plan_sections[
                  slideColorMap[currentSlide].index
                ]}
                nextSectionStartingSlidesIndex={
                  userLessonPlan?.user_lesson_plan_sections[
                    slideColorMap[currentSlide].index + 1
                  ]?.starting_slides_index
                }
                className="border-b-0"
                dir={isRTL ? 'rtl' : 'ltr'}
              />
            )}
          </div>
        )}
      </div>
      {/* Controls Panel */}
      <div className="w-80 gap-4 grid grid-rows-[auto_auto_1fr] overflow-y-scroll h-full">
        <NextSlidePreview
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          slideImages={slideImages}
        />
        <PresentationControls
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          isPresentationActive={isPresentationActive}
          prevSlide={prevSlide}
          nextSlide={nextSlide}
          startPresentation={startPresentation}
          stopPresentation={stopPresentation}
        />
        <SlideNavigation
          currentSlide={currentSlide}
          totalSlides={totalSlides}
          goToSlide={goToSlideWithSync}
        />
      </div>
      <PresentationFooter className="col-span-2" />
    </div>
  )
}
